import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChipSelect } from 'src/components/ChipSelect/ChipSelect';
import { FilterTabs } from 'src/components/FilterTabs/FilterTabs';
import { MonthPeriodFilter } from 'src/components/MonthPeriodFilter/MonthPeriodFilter';
import { currentNorwegianMonth } from 'src/utils/norwegianMonth';
import { Alert } from 'src/components/Alert';
import { Button } from 'src/components/Buttons/Button';
import { Section } from 'src/components/Section';
import { Organization, OrganizationType } from 'src/domain/organization';
import {
    useAdminAnalyticsCoverageHistory,
    useAdminAnalyticsPeople,
    useAdminAnalyticsActivity,
} from 'src/view/hooks/useAdminAnalytics';
import { useOrganizationB2Cs } from 'src/view/hooks/useOrganizationB2Cs';
import { downloadAnalyticsCsv } from './exportCsv';
import { TimeFreedCard } from './TimeFreedCard';
import { PeopleSummary } from './PeopleSummary';
import { MessageSummary } from './MessageSummary';
import { OrganizationComparison } from './OrganizationComparison';
import { DepartmentFilter, DepartmentSelection } from './DepartmentFilter';
import {
    AnalyticsScopeProvider,
    useScopedAnalyticsText,
} from './analyticsScope';
import './analytics.css';

export const currentMonth = currentNorwegianMonth;

/** Global Mundu dashboard across organizations, for global administrators. */
export const AdminAnalytics = () => {
    const { organizations } = useOrganizationB2Cs();
    return <AnalyticsDashboard availableOrganizations={organizations} />;
};

/** One organization's Analytics module: only its data, links open directly. */
export const OrganizationAnalyticsDashboard = ({
    organization,
    canOpenLinks,
    initialMonth,
}: {
    organization: Organization;
    canOpenLinks: boolean;
    initialMonth?: string;
}) => (
    <AnalyticsScopeProvider
        scope={{
            kind: 'organization',
            organizationId: organization.id,
            canOpenLinks,
        }}
    >
        <AnalyticsDashboard
            availableOrganizations={[organization]}
            organization={organization}
            initialMonth={initialMonth}
        />
    </AnalyticsScopeProvider>
);

const AnalyticsDashboard = ({
    availableOrganizations,
    organization,
    initialMonth,
}: {
    availableOrganizations: Organization[];
    /** Set inside an organization's Analytics module. */
    organization?: Organization;
    initialMonth?: string;
}) => {
    const { t, i18n } = useTranslation();
    const text = useScopedAnalyticsText();
    const organizationId = organization?.id;
    const [type, setType] = useState<OrganizationType | 'all'>('all');
    const [organizationIds, setOrganizationIds] = useState<string[]>([]);
    const [department, setDepartment] = useState<DepartmentSelection>({});
    const [month, setMonth] = useState(initialMonth ?? currentMonth);
    const [period, setPeriod] = useState<'all' | 'month'>(
        initialMonth ? 'month' : 'all',
    );
    const [customMinutes, setCustomMinutes] = useState<number | null>(null);
    const scope = {
        ...department,
        type: type === 'all' ? undefined : type,
        organizationIds: organizationIds.length
            ? [...organizationIds].sort().join(',')
            : undefined,
    };
    const people = useAdminAnalyticsPeople(scope, organizationId);
    const coverageHistory = useAdminAnalyticsCoverageHistory(
        scope,
        organizationId,
    );
    const activity = useAdminAnalyticsActivity(
        {
            ...scope,
            month: period === 'all' ? 'all' : month,
        },
        organizationId,
    );
    const { data, error, isLoading } = people;
    const periodLabel = (value: string) =>
        value === 'all' ? t('admin.analytics.allTime') : value;
    const updated = (date: string, timeZone: string) =>
        new Date(date).toLocaleString(i18n.language, { timeZone });
    const comparison =
        activity.data && data
            ? activity.data.organizations.flatMap(org => {
                  const current = data.organizations.find(
                      row => row.id === org.id,
                  );
                  return current ? [{ ...org, people: current.people }] : [];
              })
            : [];
    const changeType = (value: OrganizationType | 'all') => {
        setType(value);
        setOrganizationIds([]);
        setDepartment({});
    };
    const organizations = availableOrganizations.filter(
        org => type === 'all' || org.type === type,
    );

    return (
        <div className="analytics-dashboard">
            <header className="analytics-header">
                <h2 className="text-2xl font-semibold">
                    {t('admin.analytics.title')}
                </h2>
                <div className="analytics-global-filters">
                    {!organization && (
                        <>
                            <FilterTabs
                                label={t('admin.analytics.homeType')}
                                value={type}
                                onChange={changeType}
                                options={[
                                    {
                                        value: 'all',
                                        label: t('admin.analytics.allHomes'),
                                    },
                                    {
                                        value: OrganizationType.careHome,
                                        label: t(
                                            'admin.forms.createOrganization.typeCareHome',
                                        ),
                                    },
                                    {
                                        value: OrganizationType.nursingHome,
                                        label: t(
                                            'admin.forms.createOrganization.typeNursingHome',
                                        ),
                                    },
                                ]}
                            />
                            <ChipSelect
                                key={type}
                                className="analytics-organizations-filter"
                                ariaLabel={t('admin.analytics.organizations')}
                                options={organizations.map(org => ({
                                    id: org.id,
                                    label:
                                        org.organizationDisplayName ||
                                        org.organizationName ||
                                        org.id,
                                }))}
                                selectedIds={organizationIds}
                                onChange={ids => {
                                    setOrganizationIds(ids);
                                    setDepartment({});
                                }}
                                placeholder={t(
                                    'admin.analytics.allOrganizations',
                                )}
                                disabled={isLoading}
                            />
                        </>
                    )}
                    {(organization || organizationIds.length > 0) && (
                        <DepartmentFilter
                            organizations={
                                organization
                                    ? [organization]
                                    : organizations.filter(org =>
                                          organizationIds.includes(org.id),
                                      )
                            }
                            selection={department}
                            onChange={setDepartment}
                            disabled={isLoading}
                        />
                    )}
                    <div className="analytics-refresh">
                        <Button
                            title={t('admin.analytics.exportCsv')}
                            disabled={
                                !data ||
                                !activity.data ||
                                activity.isPreviousData ||
                                !!error ||
                                !!activity.error ||
                                isLoading ||
                                activity.isLoading
                            }
                            onClick={() => {
                                if (data && activity.data)
                                    downloadAnalyticsCsv(
                                        data,
                                        activity.data,
                                        customMinutes,
                                    );
                            }}
                        />
                        <Button
                            title={t('admin.analytics.refresh')}
                            onClick={() => {
                                void people.mutate().catch(() => undefined);
                                void activity.mutate().catch(() => undefined);
                                void coverageHistory
                                    .mutate()
                                    .catch(() => undefined);
                            }}
                            disabled={
                                people.isValidating || activity.isValidating
                            }
                        />
                    </div>
                </div>
                <p className="analytics-note">
                    {text('admin.analytics.scopeNote')}
                </p>
                {department.departmentId && (
                    <p className="analytics-note">
                        {t('admin.analytics.departmentScope')}
                    </p>
                )}
            </header>
            {error && (
                <Alert
                    isSuccess={false}
                    message={t('admin.analytics.error')}
                    canClose={false}
                />
            )}
            {isLoading && <p role="status">{t('admin.analytics.loading')}</p>}
            {data &&
                (!data.organizations.length ? (
                    <p>{text('admin.analytics.empty')}</p>
                ) : (
                    <PeopleSummary
                        people={data.totals}
                        organizations={data.organizations}
                        scopeCount={
                            organization
                                ? {
                                      label: t('admin.analytics.departments'),
                                      value: (organization.groups ?? []).filter(
                                          group => !group.parentId,
                                      ).length,
                                  }
                                : {
                                      label: t('admin.analytics.organizations'),
                                      value: data.organizations.length,
                                  }
                        }
                        usage={data.usage}
                        coverageHistory={coverageHistory.data}
                        historyLoading={coverageHistory.isLoading}
                        historyError={coverageHistory.error}
                        departmentFiltered={Boolean(department.departmentId)}
                        updated={t('admin.analytics.asOf', {
                            date: updated(data.asOf, data.timeZone),
                        })}
                    />
                ))}
            <Section
                className="analytics-section analytics-monthly-section"
                title={t('admin.analytics.activity')}
                action={
                    <MonthPeriodFilter
                        className="analytics-period-filters"
                        label={t('admin.analytics.period')}
                        allTimeLabel={t('admin.analytics.allTime')}
                        selectedMonthLabel={t('admin.analytics.selectedMonth')}
                        monthLabel={t('admin.analytics.reportingMonth')}
                        period={period}
                        month={month}
                        onPeriodChange={setPeriod}
                        onMonthChange={setMonth}
                    />
                }
            >
                {activity.error && (
                    <Alert
                        isSuccess={false}
                        message={t('admin.analytics.error')}
                        canClose={false}
                    />
                )}
                {activity.isLoading && (
                    <p role="status">{t('admin.analytics.loading')}</p>
                )}
                {activity.data && (
                    <>
                        <p className="analytics-note" aria-live="polite">
                            {t('admin.analytics.activityAsOf', {
                                period: periodLabel(activity.data.month),
                                date: updated(
                                    activity.data.asOf,
                                    activity.data.timeZone,
                                ),
                            })}{' '}
                            {t('admin.analytics.monthlyScope')}
                            {activity.isPreviousData &&
                                ' ' + t('admin.analytics.previousPeriod')}
                        </p>
                        {activity.data.organizations.length ? (
                            <div className="analytics-monthly-content">
                                <MessageSummary
                                    data={activity.data}
                                    selectedMonth={
                                        period === 'month' ? month : undefined
                                    }
                                    departmentFiltered={Boolean(
                                        department.departmentId,
                                    )}
                                    onSelectMonth={value => {
                                        setMonth(value);
                                        setPeriod('month');
                                    }}
                                    residentCount={data?.totals.residents}
                                    timeFreed={
                                        <TimeFreedCard
                                            messages={
                                                activity.data.totals.messages
                                            }
                                            customMinutes={customMinutes}
                                            onChange={setCustomMinutes}
                                        />
                                    }
                                />
                                {!organization && !!comparison.length && (
                                    <OrganizationComparison
                                        organizations={comparison}
                                    />
                                )}
                            </div>
                        ) : (
                            <p>{text('admin.analytics.empty')}</p>
                        )}
                    </>
                )}
            </Section>
        </div>
    );
};
